import axios from 'axios';
import ENDPOINTS from './endpoints';
import {
  REFRESH_KEY,
  TOKEN_KEY,
  clearStoredSession,
  forgetDevice,
  getRememberedRefreshToken,
  isTokenValid,
} from '../utils/token';

/* The API host comes from the environment so every deploy points at its own
   backend. Include the /v1 prefix in the value — the whole thing is the base.
   CRA inlines REACT_APP_* at build time, so this is fixed when `npm run build`
   runs: set it in the build environment, not on the server afterwards.
   Without it requests fall back to the app's own origin, which only works when
   something in front of the build reverse-proxies /v1 to the API — otherwise
   the static host answers POSTs with 405 Method Not Allowed. */
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '/v1').replace(/\/+$/, '');

if (!process.env.REACT_APP_API_BASE_URL && process.env.NODE_ENV !== 'production') {
  console.warn('REACT_APP_API_BASE_URL is not set — API calls will go to this app\'s own origin (/v1).');
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

/* A remembered device outlives the 24-hour access token: once the token has
   expired, the stored refresh token buys a fresh one before the next request
   goes out. Requests that fire together share one refresh. It's a bare axios
   call so it doesn't loop back through these interceptors. */
let pendingRefresh = null;
function refreshAccessToken() {
  const refreshToken = getRememberedRefreshToken();
  if (!refreshToken) return Promise.resolve(null);
  if (!pendingRefresh) {
    pendingRefresh = axios
      .post(`${API_BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, { refresh_token: refreshToken })
      .then(({ data }) => {
        const token = data?.data?.access_token || null;
        if (token) localStorage.setItem(TOKEN_KEY, token);
        /* With refresh-token rotation on, Auth0 retires the old one and hands back a new one. */
        if (data?.data?.refresh_token) localStorage.setItem(REFRESH_KEY, data.data.refresh_token);
        return token;
      })
      .catch((error) => {
        /* The API answered, so the refresh token is no good: fall back to an
           ordinary session that ends with the expired token. A network failure
           keeps it for the next try. */
        if (error.response) forgetDevice();
        return null;
      })
      .finally(() => {
        pendingRefresh = null;
      });
  }
  return pendingRefresh;
}

apiClient.interceptors.request.use(async (config) => {
  let token = localStorage.getItem(TOKEN_KEY);
  if (token && !isTokenValid(token)) token = (await refreshAccessToken()) || token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  /* A FormData body has to leave as multipart with the browser's own boundary.
     Under the instance's application/json default, axios 1.x instead
     serializes the form to JSON — every File in it becomes {} and the server
     never sees an upload. Dropping the header here lets the browser set it. */
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) config.headers.setContentType(false);
  return config;
});

apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    /* Server-side session expiry: drop the token and hard-redirect to login
       (the full reload re-hydrates the auth store from scratch). */
    if (error.response?.status === 401) {
      clearStoredSession();
      window.location.assign('/login');
    }
    const message = error.response?.data?.message || error.message || 'Something went wrong';
    const err = new Error(message);
    err.status = error.response?.status;
    return Promise.reject(err);
  }
);

export default apiClient;
